import styled, { css } from 'styled-components';

import { rem } from 'approot/shared/style';
import { borderRadius8 } from 'approot/shared/styles/borders.style';
import {
  grey1,
  grey2,
  grey5,
  grey10,
} from 'approot/shared/styles/colours.style';
import {
  spacing4,
  spacing8,
  spacing12,
  spacing16,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import { SecondaryBaseStyled } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  AnchorStyled,
  font16,
  fontSize16LineHeight,
  LinkStyled,
  PStyled,
} from 'approot/shared/styles/typography.style';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import {
  RichtextProps,
  baseSmallTextCss,
  baseTextCss,
} from 'approot/shared/richtext/richtext.style';
import {
  FlexCentreAlign,
  flexCentreAlignCss,
  flexColumnCss,
  baseFlexCss,
} from 'approot/shared/styles/flex.style';
import { viewport16by9Ratio, viewportMaxCss } from './playable.style';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import { ImageThumbnailWrapper } from 'approot/shared/images/images.style';

const baseButton = css`
  ${SecondaryBaseStyled}
  border-radius: ${rem(borderRadius8)};
  display: flex;
  align-items: center;
  height: auto;
  line-height: ${rem(fontSize16LineHeight)};
  padding: ${rem(spacing16)};
  text-align: left;
  width: 100%;
  white-space: normal;
`;

export const ComponentEmptyButtonIcon = styled(FlexCentreAlign)`
  path {
    stroke: ${grey5};
    fill: none;
  }
`;

export const ComponentEmptyButtonImage = styled.img`
  width: ${rem(100)};
`;

type ComponentEmptyButtonProps = {
  $isSmall?: boolean;
};

export const ComponentEmptyButton = styled.div<ComponentEmptyButtonProps>`
  flex-direction: column;
  background-color: ${grey2};
  border: 1px dashed ${grey5};
  border-radius: ${rem(borderRadius8)};

  & > div {
    ${flexCentreAlignCss}
    flex-direction: column;
    padding: ${rem(spacing16)} 0;
  }

  ${({ $isSmall }) =>
    !$isSmall &&
    css`
      ${viewport16by9Ratio}
      & > div {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
      }
    `}

  ${ComponentEmptyButtonIcon} {
    margin-bottom: ${({ $isSmall }) => rem($isSmall ? spacing16 : spacing24)};
  }
`;

export const ComponentEmptyButtonWrapper = styled.div`
  ${viewportMaxCss}
`;

export const ComponentItemText = styled.div`
  ${font16}
  color: ${grey10};
  margin-bottom: ${rem(spacing4)};
`;

export const ComponentItemImage = styled.div`
  border-radius: ${rem(borderRadius8)};
  border: 1px solid ${grey5};
  width: 100%;
  height: 100%;
  max-width: ${rem(64)};
  max-height: ${rem(64)};
  position: relative;
  overflow: hidden;
  margin-right: ${rem(spacing12)};

  ${ImageThumbnailWrapper} {
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  &:before {
    content: '';
    width: 100%;
    padding-top: 100%;
    display: inline-block;
  }
`;

export const ComponentItemImageNarrow = styled(ComponentItemImage)`
  margin-right: 0;
  margin-bottom: ${rem(spacing16)};
`;

export const ComponentItemContent = styled.div`
  ${baseFlexCss}
  width: 100%;
`;

export const ComponentItemContentNarrow = styled.div`
  ${flexColumnCss}
  align-items: flex-start;
  width: 100%;
`;

export const ComponentItemCol = styled.div`
  flex-grow: 1;
`;

const baseLinkCss = css`
  overflow: hidden;
  padding: ${rem(spacing12)};
  text-decoration: none;
`;

export const ComponentLinkItem = styled.a`
  ${baseButton}
  ${baseLinkCss}
`;

export const ComponentLinkPreviewItem = styled.div`
  ${baseLinkCss}
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
`;

export const ComponentLinkIconWrapper = styled.div.attrs<{
  'data-selector'?: string;
}>({
  'data-selector': 'component-link-icon',
})`
  ${flexCentreAlignCss}

  position: absolute;
  top: ${rem(spacing12)};
  right: ${rem(spacing12)};
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
  background-color: ${grey1};
  width: ${rem(32)};
  height: ${rem(32)};
`;

export const ComponentTextBadge = styled(BadgeSmallStyled).attrs<{
  'data-selector'?: string;
}>({
  'data-selector': 'component-text-badge',
})`
  display: inline-flex;
  align-items: center;
`;

export const ComponentBadgeWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export const ComponentDisplayDetails = styled.div`
  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-top: none;
  border-radius: 0 0 ${rem(borderRadius8)} ${rem(borderRadius8)};
  padding: ${rem(spacing16)};
`;

export const ComponentDisplayName = styled.h3<RichtextProps>`
  margin-bottom: ${rem(spacing4)};
  ${baseTextCss}
`;

export const ComponentDisplayDescription = styled(PStyled).attrs({
  as: 'div',
  marginBottom: 0,
})<RichtextProps>`
  ${baseSmallTextCss}

  p:last-child {
    margin-bottom: ${rem(spacing8)};
  }
`;

const linkBaseCss = css`
  text-decoration: none;
  svg {
    margin-left: ${rem(spacing12)};
    stroke: ${grey10};
  }
`;

export const ComponentDisplayLink = styled(LinkStyled)`
  ${linkBaseCss}
`;

export const ComponentDisplayAnchor = styled(AnchorStyled)`
  ${linkBaseCss}
`;

export const ComponentResponseTextArea = styled(AutoResizingTextArea).attrs({
  rows: 4,
})``;
